import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState, NoDataIcon } from '@/components/ui/EmptyState';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { StrategyBadge } from '@/components/ui/StrategyBadge';
import { PnlDisplay } from '@/components/ui/PnlDisplay';
import { StockQuoteCompact } from '@/components/ui/StockQuote';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { useChains, useOpenPnl } from '@/hooks/queries/useChains';
import { useQuotes } from '@/hooks/queries/useQuotes';
import { formatDate, formatDTE, getDaysToExpiration, toLocalDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { stockApi, type StockPosition } from '@/lib/api/client';
import { useQuery } from '@tanstack/react-query';
import { useUrlParam, useSetUrlParams, useDebouncedUrlParam } from '@/hooks/useUrlState';

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'partial', label: 'Partial' },
];

const riskOptions = [
  { value: '', label: 'All Risk Levels' },
  { value: 'danger', label: 'Danger' },
  { value: 'watch', label: 'Watch' },
  { value: 'safe', label: 'Safe' },
];

const strategyOptions = [
  { value: '', label: 'All Strategies' },
  { value: 'short_put', label: 'Short Put' },
  { value: 'short_call', label: 'Short Call' },
  { value: 'put_spread', label: 'Put Spread' },
  { value: 'call_spread', label: 'Call Spread' },
  { value: 'iron_condor', label: 'Iron Condor' },
  { value: 'strangle', label: 'Strangle' },
  { value: 'straddle', label: 'Straddle' },
  { value: 'custom', label: 'Custom' },
];

const outcomeOptions = [
  { value: '', label: 'All Outcomes' },
  { value: 'expired', label: 'Expired' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'exercised', label: 'Exercised' },
  { value: 'closed', label: 'Closed' },
  { value: 'mixed', label: 'Mixed' },
];

type PositionOutcome = 'expired' | 'assigned' | 'exercised' | 'closed' | 'mixed' | null;

function getChainOutcome(chain: { positions: Array<{ legs: Array<{ closeReason?: string | null }> }>, status: string }): PositionOutcome {
  if (chain.status !== 'closed') return null;

  // Collect all close reasons from all legs across all positions
  const closeReasons = new Set<string>();
  for (const position of chain.positions) {
    for (const leg of position.legs) {
      if (leg.closeReason) {
        closeReasons.add(leg.closeReason);
      }
    }
  }

  if (closeReasons.size === 0) return null;
  if (closeReasons.size === 1) {
    const reason = Array.from(closeReasons)[0];
    if (reason === 'expired' || reason === 'assigned' || reason === 'exercised' || reason === 'closed') {
      return reason;
    }
  }
  // Multiple different reasons = mixed
  return 'mixed';
}

function getOutcomeBadge(outcome: PositionOutcome): { label: string; className: string } | null {
  if (!outcome) return null;
  switch (outcome) {
    case 'expired': return { label: 'Expired', className: 'bg-gray-100 text-gray-600' };
    case 'assigned': return { label: 'Assigned', className: 'bg-amber-100 text-amber-700' };
    case 'exercised': return { label: 'Exercised', className: 'bg-purple-100 text-purple-700' };
    case 'closed': return { label: 'Closed', className: 'bg-green-100 text-green-700' };
    case 'mixed': return { label: 'Mixed', className: 'bg-blue-100 text-blue-700' };
    default: return null;
  }
}

type SortField = 'symbol' | 'expiration' | 'premium' | 'pnl' | 'opened';
type SortDirection = 'asc' | 'desc';

type StatusFilter = 'all' | 'open' | 'closed' | 'partial';

const SORT_FIELDS: readonly SortField[] = ['symbol', 'expiration', 'premium', 'pnl', 'opened'];
const SORT_DIRECTIONS: readonly SortDirection[] = ['asc', 'desc'];
// Each list is the option values of its own Select, so an unknown value in a stale link falls
// back to the option that is actually showing rather than filtering everything away.
const STATUS_VALUES = statusOptions.map((o) => o.value) as readonly StatusFilter[];
const RISK_VALUES: readonly string[] = riskOptions.map((o) => o.value);
const STRATEGY_VALUES: readonly string[] = strategyOptions.map((o) => o.value);
const OUTCOME_VALUES: readonly string[] = outcomeOptions.map((o) => o.value);

export function ChainList() {
  const navigate = useNavigate();

  // Every filter reads straight from the URL through the shared hook — validated against the
  // options each Select actually offers, so a stale link cannot select a value that is not there.
  const [status, setStatus] = useUrlParam<StatusFilter>('status', 'open', STATUS_VALUES);
  const [riskFilter, setRiskFilter] = useUrlParam<string>('risk', '', RISK_VALUES);
  const [strategyFilter, setStrategyFilter] = useUrlParam<string>('strategy', '', STRATEGY_VALUES);
  const [outcomeFilter, setOutcomeFilter] = useUrlParam<string>('outcome', '', OUTCOME_VALUES);
  const [sortField] = useUrlParam<SortField>('sort', 'symbol', SORT_FIELDS);
  const [sortDirection] = useUrlParam<SortDirection>('dir', 'asc', SORT_DIRECTIONS);
  const [symbolInput, handleSymbolChange] = useDebouncedUrlParam('symbol', '', 400);
  const [symbol] = useUrlParam('symbol', '');
  const setParams = useSetUrlParams();

  const { data: chains, isLoading } = useChains({
    status: status === 'all' ? undefined : (status || undefined),
    symbol: symbol || undefined
  });

  // Fetch open stock positions (only show on open/all status views)
  const showStockPositions = status === 'open' || status === 'all';
  const { data: stockPositions } = useQuery({
    queryKey: ['stock-positions', 'open'],
    queryFn: stockApi.getOpen,
    enabled: showStockPositions,
  });

  // Get unique symbols for quote fetching (include stock position symbols)
  const symbols = useMemo(() => {
    const syms = new Set<string>();
    if (chains) chains.forEach(c => syms.add(c.underlyingSymbol));
    if (stockPositions) stockPositions.forEach(sp => syms.add(sp.underlyingSymbol));
    return [...syms];
  }, [chains, stockPositions]);

  // Fetch quotes for all symbols
  const { data: quotes, isLoading: quotesLoading } = useQuotes(symbols);

  // Fetch estimated P&L for open chains
  const hasOpenChains = status !== 'closed';
  const { data: openPnl } = useOpenPnl(hasOpenChains);

  // Group stock positions by symbol for display
  const stockBySymbol = useMemo(() => {
    if (!stockPositions) return new Map<string, { totalShares: number; totalCost: number; positions: StockPosition[] }>();
    const map = new Map<string, { totalShares: number; totalCost: number; positions: StockPosition[] }>();
    for (const sp of stockPositions) {
      const existing = map.get(sp.underlyingSymbol) || { totalShares: 0, totalCost: 0, positions: [] };
      existing.totalShares += sp.quantityRemaining;
      existing.totalCost += sp.quantityRemaining * sp.costBasisPerShare;
      existing.positions.push(sp);
      map.set(sp.underlyingSymbol, existing);
    }
    return map;
  }, [stockPositions]);

  // Helper to get the closest short strike from open position
  const getClosestShortStrike = (chain: typeof chains extends (infer T)[] | undefined ? T : never, currentPrice: number | null) => {
    const openPosition = chain.positions.find((p) => p.status === 'open');
    if (!openPosition || openPosition.legs.length === 0) return null;

    // Find short legs (STO/STC)
    const shortLegs = openPosition.legs.filter(l => l.action === 'STO' || l.action === 'STC');
    if (shortLegs.length === 0) {
      // Fallback to first leg if no short legs
      const primaryLeg = openPosition.legs[0];
      return { strike: primaryLeg.strikePrice, optionType: primaryLeg.optionType };
    }

    // Find the closest short strike to current price
    if (currentPrice) {
      const closest = shortLegs.reduce((prev, curr) =>
        Math.abs(currentPrice - curr.strikePrice) < Math.abs(currentPrice - prev.strikePrice) ? curr : prev
      );
      return { strike: closest.strikePrice, optionType: closest.optionType };
    }

    return { strike: shortLegs[0].strikePrice, optionType: shortLegs[0].optionType };
  };

  // Calculate risk level for a chain based on distance to short strikes
  const getRiskLevel = (chain: typeof chains extends (infer T)[] | undefined ? T : never, currentPrice: number | null): 'safe' | 'watch' | 'danger' | null => {
    if (chain.status !== 'open' || !currentPrice) return null;

    const openPosition = chain.positions.find((p) => p.status === 'open');
    if (!openPosition) return null;

    // Find short legs
    const shortLegs = openPosition.legs.filter(l => l.action === 'STO' || l.action === 'STC');
    if (shortLegs.length === 0) return null;

    // Find closest distance to any short strike
    let closestPercent = Infinity;
    for (const leg of shortLegs) {
      const distance = leg.optionType === 'put'
        ? currentPrice - leg.strikePrice  // For puts, price should be above strike
        : leg.strikePrice - currentPrice; // For calls, price should be below strike
      const percent = (distance / leg.strikePrice) * 100;
      if (percent < closestPercent) {
        closestPercent = percent;
      }
    }

    if (closestPercent <= 0) return 'danger'; // ITM
    if (closestPercent < 3) return 'danger';
    if (closestPercent < 7) return 'watch';
    return 'safe';
  };

  const getEarliestExpiration = (chain: typeof chains extends (infer T)[] | undefined ? T : never) => {
    let earliest: Date | null = null;
    for (const position of chain.positions) {
      if (position.status !== 'open') continue;
      for (const leg of position.legs) {
        const exp = toLocalDate(leg.expirationDate);
        if (!earliest || exp < earliest) {
          earliest = exp;
        }
      }
    }
    return earliest;
  };

  // Filter and sort chains
  const filteredAndSortedChains = useMemo(() => {
    if (!chains) return [];

    // Filter by risk level
    let filtered = chains;
    if (riskFilter) {
      filtered = filtered.filter(chain => {
        const quote = quotes?.[chain.underlyingSymbol];
        const risk = getRiskLevel(chain, quote?.price ?? null);
        return risk === riskFilter;
      });
    }

    // Filter by strategy
    if (strategyFilter) {
      filtered = filtered.filter(chain => chain.strategyType === strategyFilter);
    }

    // Filter by outcome
    if (outcomeFilter) {
      filtered = filtered.filter(chain => {
        const outcome = getChainOutcome(chain);
        return outcome === outcomeFilter;
      });
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'symbol':
          comparison = a.underlyingSymbol.localeCompare(b.underlyingSymbol);
          break;
        case 'expiration':
          const expA = getEarliestExpiration(a);
          const expB = getEarliestExpiration(b);
          if (!expA && !expB) comparison = 0;
          else if (!expA) comparison = 1;
          else if (!expB) comparison = -1;
          else comparison = expA.getTime() - expB.getTime();
          break;
        case 'premium':
          const premiumA = a.totalPremiumCollected - a.totalPremiumPaid;
          const premiumB = b.totalPremiumCollected - b.totalPremiumPaid;
          comparison = premiumA - premiumB;
          break;
        case 'pnl': {
          const pnlA = a.status === 'closed' ? a.realizedPnl : (openPnl?.[a.id]?.estimatedPnl ?? 0);
          const pnlB = b.status === 'closed' ? b.realizedPnl : (openPnl?.[b.id]?.estimatedPnl ?? 0);
          comparison = pnlA - pnlB;
          break;
        }
        case 'opened':
          comparison = new Date(a.openedAt).getTime() - new Date(b.openedAt).getTime();
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [chains, quotes, openPnl, riskFilter, strategyFilter, outcomeFilter, sortField, sortDirection]);

  // Field and direction move together, so they go out in one write.
  const handleSort = (field: SortField) => {
    const nextDirection: SortDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setParams({
      sort: field === 'symbol' ? null : field,
      dir: nextDirection === 'asc' ? null : nextDirection,
    });
  };

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <button
      onClick={() => handleSort(field)}
      data-testid={`sort-${field}`}
      aria-pressed={sortField === field}
      className={cn(
        'flex items-center gap-1 hover:text-gray-900 transition-colors',
        sortField === field ? 'text-gray-900 font-semibold' : ''
      )}
    >
      {children}
      {sortField === field && (
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          {sortDirection === 'asc' ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          )}
        </svg>
      )}
    </button>
  );

  const [filtersOpen, setFiltersOpen] = useState(false);

  if (isLoading) {
    return (
      <Card padding="lg" className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </Card>
    );
  }

  // Count active filters (beyond defaults)
  const activeFilterCount = [
    symbol,
    status !== 'open' ? status : '',
    riskFilter,
    strategyFilter,
    outcomeFilter,
  ].filter(Boolean).length;

  return (
    <div className="space-y-4">
      {/* Mobile filter toggle + New Trade */}
      <div className="flex items-center justify-between sm:hidden">
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700"
        >
          <svg className={cn('w-4 h-4 transition-transform', filtersOpen && 'rotate-180')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          Filters
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-brand-100 text-brand-700 text-xs font-semibold">
              {activeFilterCount}
            </span>
          )}
        </button>
        <Link to="/chains/new">
          <Button size="sm">New Trade</Button>
        </Link>
      </div>

      {/* Filters — always visible on desktop, collapsible on mobile */}
      <div className={cn(
        'flex-col sm:flex sm:flex-row gap-4 flex-wrap',
        filtersOpen ? 'flex' : 'hidden sm:flex'
      )}>
        <div className="flex-1 min-w-[150px]">
          <Input
            placeholder="Search by symbol..."
            value={symbolInput}
            onChange={(e) => handleSymbolChange(e.target.value.toUpperCase())}
          />
        </div>
        <div className="w-full sm:w-36">
          <Select
            options={statusOptions}
            value={status}
            data-testid="filter-status"
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
          />
        </div>
        <div className="w-full sm:w-40">
          <Select
            options={riskOptions}
            value={riskFilter}
            data-testid="filter-risk"
            onChange={(e) => setRiskFilter(e.target.value)}
          />
        </div>
        <div className="w-full sm:w-40">
          <Select
            options={strategyOptions}
            value={strategyFilter}
            data-testid="filter-strategy"
            onChange={(e) => setStrategyFilter(e.target.value)}
          />
        </div>
        {status === 'closed' && (
          <div className="w-full sm:w-36">
            <Select
              options={outcomeOptions}
              value={outcomeFilter}
              data-testid="filter-outcome"
              onChange={(e) => setOutcomeFilter(e.target.value)}
            />
          </div>
        )}
        <div className="hidden sm:block">
          <Link to="/chains/new">
            <Button>New Trade</Button>
          </Link>
        </div>
      </div>

      {/* Chain count */}
      {filteredAndSortedChains.length > 0 && (
        <div className="text-sm text-gray-500">
          {filteredAndSortedChains.length} {filteredAndSortedChains.length === 1 ? 'chain' : 'chains'}
          {chains && filteredAndSortedChains.length !== chains.length && ` (of ${chains.length} total)`}
        </div>
      )}

      {/* Chain list */}
      {filteredAndSortedChains.length === 0 ? (
        <Card padding="none">
          <EmptyState
            icon={<NoDataIcon />}
            title="No trade chains found"
            description={symbol || status || riskFilter || strategyFilter || outcomeFilter ? 'Try adjusting your filters' : 'Create your first trade to get started'}
            action={
              <Link to="/chains/new">
                <Button>New Trade</Button>
              </Link>
            }
            className="py-12"
          />
        </Card>
      ) : (
        <>
          {/* Mobile card view */}
          <div className="space-y-2 sm:hidden">
            {/* Stock holding cards */}
            {showStockPositions && [...stockBySymbol.entries()].map(([sym, holding]) => {
              const quote = quotes?.[sym];
              const marketValue = quote?.price ? quote.price * holding.totalShares : null;
              return (
                <div key={`stock-${sym}`} className="bg-white rounded-lg border border-gray-200 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{sym}</span>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                          Stock
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>{holding.totalShares} shares</span>
                        {quote?.price && <span>${quote.price.toFixed(2)}</span>}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {marketValue !== null && (
                        <>
                          <span className="block text-[10px] text-gray-400 leading-tight">Value</span>
                          <span className="text-sm font-mono text-gray-900">
                            ${marketValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {filteredAndSortedChains.map((chain) => {
              const expiration = getEarliestExpiration(chain);
              const dte = expiration ? getDaysToExpiration(expiration) : null;
              const netPremium = chain.totalPremiumCollected - chain.totalPremiumPaid;
              const quote = quotes?.[chain.underlyingSymbol];
              const riskLevel = getRiskLevel(chain, quote?.price ?? null);
              const pnlValue = chain.status === 'closed'
                ? chain.realizedPnl
                : openPnl?.[chain.id]?.estimatedPnl ?? null;

              return (
                <div
                  key={chain.id}
                  className="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer active:bg-gray-50"
                  onClick={() => navigate(`/chains/${chain.id}`)}
                >
                  <div className="flex items-start justify-between gap-2">
                    {/* Left: symbol + details */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {riskLevel && (
                          <span className={cn(
                            'w-2 h-2 rounded-full flex-shrink-0',
                            riskLevel === 'safe' && 'bg-green-500',
                            riskLevel === 'watch' && 'bg-yellow-500',
                            riskLevel === 'danger' && 'bg-red-500'
                          )} />
                        )}
                        <span className="font-semibold text-gray-900">{chain.underlyingSymbol}</span>
                        <StrategyBadge strategy={chain.strategyType} size="sm" />
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        {quote?.price && (
                          <span>${quote.price.toFixed(2)}</span>
                        )}
                        {expiration && dte !== null && (
                          <span className={cn(
                            dte <= 7 && dte >= 0 ? 'text-yellow-600 font-medium' : ''
                          )}>
                            {formatDTE(expiration)}
                          </span>
                        )}
                        <span>
                          Credit: {netPremium >= 0 ? '' : '-'}${Math.abs(netPremium).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                    {/* Right: P&L */}
                    <div className="text-right flex-shrink-0">
                      {pnlValue !== null ? (
                        <>
                          <span className="block text-[10px] text-gray-400 leading-tight">
                            {chain.status === 'closed' ? 'P&L' : 'Est. P&L'}
                          </span>
                          <PnlDisplay value={pnlValue} size="sm" />
                        </>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                      {chain.status === 'closed' && (() => {
                        const outcome = getChainOutcome(chain);
                        const badge = getOutcomeBadge(outcome);
                        if (!badge) return null;
                        return (
                          <span className={cn('block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium', badge.className)}>
                            {badge.label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table view */}
          <Card padding="none" className="hidden sm:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead><SortableHeader field="symbol">Symbol</SortableHeader></TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Strategy</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead><SortableHeader field="expiration">Expiration</SortableHeader></TableHead>
                  <TableHead className="text-right"><SortableHeader field="premium">Premium</SortableHeader></TableHead>
                  <TableHead className="text-right"><SortableHeader field="pnl">{status !== 'closed' ? 'P&L (est)' : 'P&L'}</SortableHeader></TableHead>
                  {status === 'closed' && <TableHead>Outcome</TableHead>}
                  <TableHead><SortableHeader field="opened">Opened</SortableHeader></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Stock holding rows */}
                {showStockPositions && [...stockBySymbol.entries()]
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([sym, holding]) => {
                    const quote = quotes?.[sym];
                    const marketValue = quote?.price ? quote.price * holding.totalShares : null;
                    const avgCost = holding.totalCost / holding.totalShares;
                    return (
                      <TableRow key={`stock-${sym}`} className="bg-gray-50/50">
                        <TableCell>
                          <Link
                            to={`/symbols/${sym}`}
                            className="font-semibold text-brand-600 hover:text-brand-700"
                          >
                            {sym}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {quote?.price ? (
                            <span className="text-sm text-gray-700">${quote.price.toFixed(2)}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                            Stock
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-700">{holding.totalShares} shares</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-500">Avg ${avgCost.toFixed(2)}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-gray-400">-</span>
                        </TableCell>
                        <TableCell className="text-right">
                          {marketValue !== null ? (
                            <span className="font-mono text-sm text-gray-700">
                              ${marketValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-gray-500 text-sm">
                          {formatDate(holding.positions[0].openedAt)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {filteredAndSortedChains.map((chain) => {
                  const expiration = getEarliestExpiration(chain);
                  const dte = expiration ? getDaysToExpiration(expiration) : null;
                  const netPremium = chain.totalPremiumCollected - chain.totalPremiumPaid;
                  const quote = quotes?.[chain.underlyingSymbol];
                  const closestShortStrike = getClosestShortStrike(chain, quote?.price ?? null);
                  const riskLevel = getRiskLevel(chain, quote?.price ?? null);

                  return (
                    <TableRow
                      key={chain.id}
                      data-testid={`chain-row-${chain.underlyingSymbol}`}
                      className="cursor-pointer"
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('a, button')) return;
                        navigate(`/chains/${chain.id}`);
                      }}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {riskLevel && (
                            <span
                              className={cn(
                                'w-2 h-2 rounded-full flex-shrink-0',
                                riskLevel === 'safe' && 'bg-green-500',
                                riskLevel === 'watch' && 'bg-yellow-500',
                                riskLevel === 'danger' && 'bg-red-500'
                              )}
                              title={`Risk: ${riskLevel}`}
                            />
                          )}
                          <Link
                            to={`/symbols/${chain.underlyingSymbol}`}
                            className="font-semibold text-brand-600 hover:text-brand-700"
                            title={`View all ${chain.underlyingSymbol} chains`}
                          >
                            {chain.underlyingSymbol}
                          </Link>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StockQuoteCompact
                          price={quote?.price ?? null}
                          strikePrice={closestShortStrike?.strike}
                          optionType={closestShortStrike?.optionType}
                          isLoading={quotesLoading}
                        />
                      </TableCell>
                      <TableCell>
                        <StrategyBadge strategy={chain.strategyType} size="sm" />
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={chain.status} size="sm" />
                      </TableCell>
                      <TableCell>
                        {expiration && dte !== null ? (
                          <span
                            className={cn(
                              'text-sm',
                              dte <= 7 && dte >= 0 ? 'text-yellow-600 font-medium' : 'text-gray-600'
                            )}
                          >
                            {formatDTE(expiration)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono text-sm">{netPremium >= 0 ? '+' : ''}{netPremium.toFixed(2)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        {chain.status === 'closed' ? (
                          <PnlDisplay value={chain.realizedPnl} size="sm" />
                        ) : openPnl?.[chain.id] ? (
                          <PnlDisplay value={openPnl[chain.id].estimatedPnl} size="sm" />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      {status === 'closed' && (
                        <TableCell>
                          {(() => {
                            const outcome = getChainOutcome(chain);
                            const badge = getOutcomeBadge(outcome);
                            if (!badge) return <span className="text-gray-400">-</span>;
                            return (
                              <span className={cn('px-1.5 py-0.5 rounded text-xs font-medium', badge.className)}>
                                {badge.label}
                              </span>
                            );
                          })()}
                        </TableCell>
                      )}
                      <TableCell className="text-gray-500 text-sm">
                        {formatDate(chain.openedAt)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
